import { notify } from '@wyz/ui';
import { authHandlers, callApi, invariant } from '@wyz/utils';
import React, { useCallback, useMemo } from 'react';
import { useConfig, useDebugContext, Trans } from '~/common/context';
import { log } from '~/common/helpers';
import { useAuth } from 'react-oidc-context';
// eslint-disable-next-line @wyz/module/no-relative-import
import { ApiContext } from './contexts';
import { IApiContext } from '~/common/types';

export function ApiProvider(props: React.PropsWithChildren) {
  const auth = useAuth();
  const { apiUrl, wyzFlowApiUrl } = useConfig();
  const isDebugMode = useDebugContext();
  const onExpire = useCallback(() => {
    notify.warning(<Trans i18nKey='app.common.session.expired_message' />, {
      id: 'toast-warning-session-expired',
    });
    auth
      .signoutSilent({
        extraQueryParams: {
          ref: location.pathname, // TODO: react-oidc-context is not adding this QS to the login url :(
        },
        silentRequestTimeoutInSeconds: 4,
      })
      .catch((error: Error) => log(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const context = useMemo(() => {
    invariant(auth.user?.access_token);
    authHandlers().setToken(auth.user?.access_token);

    const coreApi = callApi({
      baseUrl: apiUrl,
      onExpire,
      isDebugModeEnabled: isDebugMode,
    });
    const flowApi = callApi({
      baseUrl: wyzFlowApiUrl,
      onExpire,
      excludeCustomHeaders: true,
      isDebugModeEnabled: isDebugMode,
      excludeTimeZone: true,
    });
    return {
      callCoreApi: coreApi,
      callFlowApi: flowApi,
      pricingPoliciesDomainBaseUrl: `/pricing-policies/api`,
      tenantsDomainBaseUrl: `/tenants/api`,
      suppliersDomainBaseUrl: `/suppliers/api`,
    } satisfies IApiContext;
  }, [auth.user?.access_token, apiUrl, wyzFlowApiUrl, onExpire, isDebugMode]);

  return (
    <ApiContext.Provider value={context}>{props.children}</ApiContext.Provider>
  );
}
export const useApi = () => {
  const context = React.useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within a ApiContext');
  }
  return context;
};
